package com.iregained.eventapp.components.layouts

import androidx.compose.runtime.*
import com.iregained.eventapp.components.sections.footer.sections.MainFooter
import com.iregained.eventapp.components.sections.header.sections.NavHeader
import com.iregained.eventapp.theme.toSitePalette
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.EventListener
import kotlin.time.Duration.Companion.milliseconds

val PageContentStyle = CssStyle {
    base { Modifier.fillMaxWidth() }
}

class PageLayoutData(val title: String)

// GLOBAL FLAG: Tracks if the current route change was caused by the browser's Back/Forward buttons
private var isNavigatingViaBrowserHistory = false

@Composable
@Layout
fun PageLayout(
    ctx: PageContext = rememberPageContext(),
    content: @Composable ColumnScope.() -> Unit
) {
    val data = ctx.data.getValue<PageLayoutData>()
    val palette = ColorMode.current.toSitePalette()

    // TITLE UPDATE
    LaunchedEffect(data.title) {
        document.title = "IRegained - ${data.title}"
    }

    // POPSTATE LISTENER: Detects Back/Forward button usage
    DisposableEffect(Unit) {
        val handlePopState = EventListener {
            isNavigatingViaBrowserHistory = true
        }
        window.addEventListener("popstate", handlePopState)

        onDispose {
            window.removeEventListener("popstate", handlePopState)
        }
    }

    // SCROLL TRACKER: Continuously save the exact Y-axis pixel position in the browser's Session Storage
    DisposableEffect(ctx.route.path) {
        onDispose {
            window.sessionStorage.setItem(ctx.route.path, window.scrollY.toString())
        }
    }

    // SCROLL RESTORATION: Combats Layout Shift from loading images across all pages
    LaunchedEffect(ctx.route.path, ctx.route.fragment) {
        val fragment = ctx.route.fragment

        if (!fragment.isNullOrBlank()) {
            // SCENARIO A: URL has a #fragment (e.g., clicking a specific anchor link)
            delay(100.milliseconds)
            document.getElementById(fragment)?.asDynamic().scrollIntoView(
                js("{ behavior: 'smooth', block: 'start' }")
            )
        } else {
            // SCENARIO B: Standard page navigation
            if (isNavigatingViaBrowserHistory) {
                // Instantly restore scroll without looping and fighting the user
                val savedScrollY = window.sessionStorage.getItem(ctx.route.path)?.toDoubleOrNull() ?: 0.0
                window.scrollTo(js("{ top: savedScrollY, behavior: 'instant' }"))
                isNavigatingViaBrowserHistory = false
            } else {
                // Instantly teleport to top on fresh load
                window.scrollTo(js("{ top: 0, behavior: 'instant' }"))
            }
        }
    }

    // MAIN LAYOUT
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier { property("min-height", "100dvh") } // Better for mobile UX
            .backgroundColor(palette.custom.surfaceBackground),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        NavHeader()

        // Content Area takes up all remaining space
        Box(modifier = Modifier.fillMaxWidth().flexGrow(1)) {
            Div(PageContentStyle.toAttrs()) {
                this@Column.content()
            }
        }

        MainFooter()
    }
}